import { useState, useCallback, useMemo, useEffect, memo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

// Google Maps types
declare const google: any;

declare global {
  interface Window {
    google: any;
  }
}

// Toast notification component
const Toast = ({
  message,
  type,
  onClose,
}: {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
}) => {
  const bgColor =
    type === 'success'
      ? 'bg-green-500'
      : type === 'error'
      ? 'bg-red-500'
      : 'bg-blue-500';
  const icon =
    type === 'success'
      ? 'ri-check-line'
      : type === 'error'
      ? 'ri-error-warning-line'
      : 'ri-information-line';

  return (
    <div
      className={`fixed top-24 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 max-w-sm`}
    >
      <i className={`${icon} text-lg`}></i>
      <span className="text-sm font-medium">{message}</span>
      <button
        onClick={onClose}
        className="ml-2 text-white hover:text-gray-200"
      >
        <i className="ri-close-line text-lg"></i>
      </button>
    </div>
  );
};

// Separate memoized component for photo upload to prevent unnecessary re-renders
const PhotoUploadSection = memo(
  ({
    imagePreviews,
    onImageUpload,
    onRemovePhoto,
  }: {
    imagePreviews: string[];
    onImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onRemovePhoto: (index: number) => void;
  }) => {
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const handleDragStart = (
      e: React.DragEvent<HTMLDivElement>,
      index: number,
    ) => {
      setDraggedIndex(index);
      e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    };

    const handleDragEnter = (
      e: React.DragEvent<HTMLDivElement>,
      index: number,
    ) => {
      e.preventDefault();
      setDragOverIndex(index);
    };

    const handleDragLeave = () => {
      setDragOverIndex(null);
    };

    const handleDrop = (
      e: React.DragEvent<HTMLDivElement>,
      dropIndex: number,
    ) => {
      e.preventDefault();

      if (draggedIndex === null || draggedIndex === dropIndex) {
        setDraggedIndex(null);
        setDragOverIndex(null);
        return;
      }

      const event = new CustomEvent('reorderPhotos', {
        detail: { from: draggedIndex, to: dropIndex },
      });
      window.dispatchEvent(event);

      setDraggedIndex(null);
      setDragOverIndex(null);
    };

    const handleDragEnd = () => {
      setDraggedIndex(null);
      setDragOverIndex(null);
    };

    return (
      <div className="bg-white rounded-2xl p-8 shadow-sm">
        <div className="flex items-center gap-3 pb-4 border-b border-gray-200 mb-6">
          <div className="w-10 h-10 bg-[#14B8A6] text-white rounded-full flex items-center justify-center font-bold">
            4
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            Add Photos of Your Space
          </h2>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Upload Photos
          </label>
          <div
            onClick={() => document.getElementById('photo-upload')?.click()}
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-[#14B8A6]', 'bg-teal-50'); }}
            onDragLeave={(e) => { e.currentTarget.classList.remove('border-[#14B8A6]', 'bg-teal-50'); }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('border-[#14B8A6]', 'bg-teal-50');
              const files = Array.from(e.dataTransfer.files).filter(f => ['image/png','image/jpeg','image/jpg'].includes(f.type));
              if (files.length > 0) {
                const syntheticEvent = { target: { files: e.dataTransfer.files } } as unknown as React.ChangeEvent<HTMLInputElement>;
                onImageUpload(syntheticEvent);
              }
            }}
            className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-[#14B8A6] transition-colors cursor-pointer bg-gray-50"
          >
            <i className="ri-image-add-line text-5xl text-gray-400 mb-4"></i>
            <p className="text-gray-600 mb-2">Click to upload or drag & drop photos here</p>
            <p className="text-sm text-gray-500">PNG, JPG, JPEG up to 10MB each</p>
            <input
              id="photo-upload"
              type="file"
              multiple
              accept="image/png,image/jpeg,image/jpg"
              onChange={onImageUpload}
              className="hidden"
            />
          </div>
        </div>

        {imagePreviews.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {imagePreviews.map((photo, index) => (
              <div
                key={`${photo}-${index}`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                className={`relative group rounded-xl overflow-hidden cursor-move transition-all ${
                  draggedIndex === index ? 'opacity-50 scale-95' : ''
                } ${
                  dragOverIndex === index ? 'ring-2 ring-[#14B8A6]' : ''
                }`}
              >
                <img
                  src={photo}
                  alt={`Upload ${index + 1}`}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-2 left-2 bg-black/50 rounded-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <i className="ri-drag-move-line text-white text-lg"></i>
                </div>
                {index === 0 && (
                  <div className="absolute top-2 right-2 bg-[#14B8A6] text-white px-3 py-1 rounded-full text-xs font-semibold">
                    ⭐ Cover Photo
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onRemovePhoto(index)}
                  className="absolute bottom-2 right-2 bg-red-500 text-white w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  },
);

PhotoUploadSection.displayName = 'PhotoUploadSection';

const spaceTypes = [
  { id: 'garage', name: 'Garage', icon: 'ri-car-line', color: 'bg-blue-50' },
  { id: 'shed', name: 'Shed', icon: 'ri-home-gear-line', color: 'bg-green-50' },
  { id: 'warehouse', name: 'Warehouse', icon: 'ri-building-line', color: 'bg-purple-50' },
  { id: 'basement', name: 'Basement', icon: 'ri-home-8-line', color: 'bg-orange-50' },
  { id: 'container', name: 'Container', icon: 'ri-inbox-line', color: 'bg-red-50' },
  { id: 'parking', name: 'Parking Space', icon: 'ri-parking-box-line', color: 'bg-yellow-50' },
  { id: 'caravan_boat', name: 'Caravan or Boat Storage', icon: 'ri-ship-line', color: 'bg-teal-50' },
  { id: 'land', name: 'Acreage / Land', icon: 'ri-landscape-line', color: 'bg-lime-50' },
  { id: 'pontoon', name: 'Pontoon / Jetty', icon: 'ri-anchor-line', color: 'bg-cyan-50' },
];

const availableFeatures = [
  '24/7 Access', 'Alarm System', 'Bathroom Access', 'Climate Controlled',
  'Covered', 'Drive-Through', 'Drive-up Access', 'Electricity', 'Fire Safety',
  'Forklift Access', 'Gated Entry', 'Ground Floor', 'High Ceiling',
  'Indoor Storage', 'Lift Access', 'Lighting', 'Loading Dock', 'Office Space',
  'On-Site Manager', 'Outdoor Storage', 'Package Acceptance', 'Pallet Storage',
  'Pest Control', 'Phone Reception', 'Racking System', 'Security Cameras',
  'Separate Access', 'Shelving Available', 'Ventilation', 'Water Access',
  'Wheelchair Access', 'Wide Entry', 'WiFi Available',
];

export default function ListSpace() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [searchParams] = useSearchParams();
  const editingId = searchParams.get('id');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    spaceType: '',
    size: '',
    address: '',
    suburb: '',
    state: '',
    postcode: '',
    price: '',
    minRentalPeriod: '1',
    exposure: '',
    availability: 'immediate',
    rentalEndDate: '',
    customAvailabilityDate: '',
    features: [] as string[],
    images: [] as File[],
    firstMonthDiscount: false,
  });

  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [deletedExistingImages, setDeletedExistingImages] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isLoadingListing, setIsLoadingListing] = useState(false);
  const [lastGeocodedAddress, setLastGeocodedAddress] = useState<string>('');
  const addressSelectedFromAutocomplete = useRef(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showValidationSummary, setShowValidationSummary] = useState(false);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    id: number;
  } | null>(null);
  const [declarationChecked, setDeclarationChecked] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);

  const showToast = (message: string, type: 'success' | 'error' | 'info') => {
    const id = Date.now();
    setToast({ message, type, id });
    setTimeout(() => setToast(null), 5000);
  };

  const hideToast = () => setToast(null);

  const isFormComplete = () => {
    return (
      formData.title.trim() !== '' &&
      formData.description.trim() !== '' &&
      formData.spaceType !== '' &&
      formData.size !== '' &&
      formData.address.trim() !== '' &&
      formData.suburb.trim() !== '' &&
      formData.state !== '' &&
      formData.postcode.trim() !== '' &&
      formData.price !== '' &&
      imagePreviews.length > 0 &&
      formData.features.length >= 3 &&
      formData.exposure !== '' &&
      declarationChecked
    );
  };

  const getMissingFields = () => {
    const missing = [];
    if (!formData.title.trim()) missing.push('Space Title');
    if (!formData.description.trim()) missing.push('Description');
    if (!formData.spaceType) missing.push('Space Type');
    if (!formData.size) missing.push('Size');
    if (!formData.address.trim()) missing.push('Street Address');
    if (!formData.suburb.trim()) missing.push('Suburb');
    if (!formData.state) missing.push('State');
    if (!formData.postcode.trim()) missing.push('Postcode');
    if (!formData.price) missing.push('Monthly Price');
    if (imagePreviews.length === 0) missing.push('At least one photo');
    if (formData.features.length < 3) missing.push('At least 3 features/amenities');
    if (!formData.exposure) missing.push('Exposure Type');
    if (!declarationChecked) missing.push('Host Declaration');
    return missing;
  };

  const validateAllFields = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.spaceType) newErrors.spaceType = 'Please select a space type';
    if (!formData.size) newErrors.size = 'Size is required';
    if (!formData.address.trim()) newErrors.address = 'Street address is required';
    if (!formData.suburb.trim()) newErrors.suburb = 'Suburb is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
    if (!formData.price) newErrors.price = 'Price is required';
    if (imagePreviews.length === 0) newErrors.images = 'At least one photo is required';
    setValidationErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setShowValidationSummary(true);
      return false;
    }
    return true;
  };

  const validateImageFile = (file: File, index: number) => {
    const maxSize = 10 * 1024 * 1024;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: `Image ${index + 1}: Only PNG, JPG, and JPEG files are allowed` };
    }
    if (file.size > maxSize) {
      return { valid: false, error: `Image ${index + 1}: File size must be less than 10MB` };
    }
    return { valid: true };
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: false }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => { const newErr = { ...prev }; delete newErr[name]; return newErr; });
    }
  };

  const handleFieldBlur = (fieldName: string, value: string) => {
    if (!value || value.trim() === '') {
      setValidationErrors((prev) => ({
        ...prev,
        [fieldName]: `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`,
      }));
    } else {
      setValidationErrors((prev) => { const newErr = { ...prev }; delete newErr[fieldName]; return newErr; });
    }
  };

  const handleAddressBlur = async () => {
    const fullAddress = `${formData.address}, ${formData.suburb}, ${formData.state} ${formData.postcode}`;
    if (fullAddress !== lastGeocodedAddress && formData.address.trim()) {
      await geocodeAddress(fullAddress);
      setLastGeocodedAddress(fullAddress);
    }
  };

  const geocodeAddress = async (address: string) => {
    if (!window.google || !window.google.maps) return;
    const geocoder = new window.google.maps.Geocoder();
    try {
      const result = await new Promise<any>((resolve, reject) => {
        geocoder.geocode({ address }, (results: any, status: any) => {
          if (status === 'OK' && results[0]) resolve(results[0]);
          else reject(new Error('Geocoding failed'));
        });
      });
      setLatitude(result.geometry.location.lat());
      setLongitude(result.geometry.location.lng());
    } catch (error) {
      if (!addressSelectedFromAutocomplete.current) {
        showToast('Could not find location. Please verify the address.', 'error');
      }
    }
  };

  const toggleFeature = (feature: string) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter((f) => f !== feature)
        : [...prev.features, feature],
    }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    for (let i = 0; i < files.length; i++) {
      const validation = validateImageFile(files[i], uploadedFiles.length + i);
      if (!validation.valid) { showToast(validation.error!, 'error'); return; }
    }
    setUploadedFiles((prev) => [...prev, ...files]);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreviews((prev) => [...prev, reader.result as string]);
      reader.readAsDataURL(file);
    });
    if (validationErrors.images) {
      setValidationErrors((prev) => { const newErr = { ...prev }; delete newErr.images; return newErr; });
    }
  };

  const removePhoto = (index: number) => {
    const preview = imagePreviews[index];
    if (existingImages.includes(preview)) {
      setDeletedExistingImages((prev) => [...prev, preview]);
      setExistingImages((prev) => prev.filter((img) => img !== preview));
    } else {
      const uploadIndex = imagePreviews.slice(0, index).filter(p => !existingImages.includes(p)).length;
      setUploadedFiles((prev) => prev.filter((_, i) => i !== uploadIndex));
    }
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePauseListing = async () => {
    if (!confirm('Are you sure you want to pause this listing? It will no longer be visible to renters.')) return;
    try {
      const { error } = await supabase.from('listings').update({ status: 'paused', is_available: false }).eq('id', editingId).eq('host_id', user?.id);
      if (error) throw error;
      showToast('Listing paused successfully', 'success');
      setTimeout(() => navigate('/my-listings'), 1500);
    } catch (error: any) {
      showToast(`Failed to pause listing: ${error.message}`, 'error');
    }
  };

  const handleDeleteListing = async () => {
    if (!confirm('Are you sure you want to delete this listing? This action cannot be undone.')) return;
    try {
      const { error } = await supabase.from('listings').update({ status: 'deleted', is_available: false }).eq('id', editingId).eq('host_id', user?.id);
      if (error) throw error;
      showToast('Listing deleted successfully', 'success');
      setTimeout(() => navigate('/my-listings'), 1500);
    } catch (error: any) {
      showToast(`Failed to delete listing: ${error.message}`, 'error');
    }
  };

  useEffect(() => {
    if (editingId && user) loadListingData();
  }, [editingId, user]);

  useEffect(() => {
    const handleReorder = (e: Event) => {
      const { from, to } = (e as CustomEvent).detail;
      setImagePreviews(prev => {
        const updated = [...prev];
        const [moved] = updated.splice(from, 1);
        updated.splice(to, 0, moved);
        return updated;
      });
    };
    window.addEventListener('reorderPhotos', handleReorder);
    return () => window.removeEventListener('reorderPhotos', handleReorder);
  }, []);

  const loadListingData = async () => {
    setIsLoadingListing(true);
    try {
      const { data: listing, error } = await supabase.from('listings').select('*').eq('id', editingId).eq('host_id', user?.id).single();
      if (error) throw error;
      if (listing) {
        setFormData({
          title: listing.title || '',
          description: listing.description || '',
          spaceType: listing.space_type || '',
          size: listing.size_sqm?.toString() || '',
          address: listing.street_address || '',
          suburb: listing.suburb || '',
          state: listing.state || '',
          postcode: listing.postcode || '',
          price: listing.price_per_month?.toString() || '',
          minRentalPeriod: listing.min_rental_period?.toString() || '1',
          availability: 'immediate',
          rentalEndDate: '',
          customAvailabilityDate: '',
          features: listing.features || [],
          images: [],
          firstMonthDiscount: listing.first_month_discount || false,
          exposure: listing.exposure || '',
        });
        setDeclarationChecked(true);
        setLatitude(listing.latitude);
        setLongitude(listing.longitude);
        if (listing.images && listing.images.length > 0) {
          setExistingImages(listing.images);
          setImagePreviews(listing.images);
        }
      }
    } catch (error: any) {
      showToast(`Failed to load listing: ${error.message}`, 'error');
    } finally {
      setIsLoadingListing(false);
    }
  };

  useEffect(() => {
    const attachAutocomplete = () => {
      const input = document.getElementById('street-address-input') as HTMLInputElement;
      if (!input) { setTimeout(attachAutocomplete, 200); return; }
      if (!window.google?.maps?.places) { setTimeout(attachAutocomplete, 200); return; }
      const autocomplete = new window.google.maps.places.Autocomplete(input, {
        componentRestrictions: { country: 'au' },
        fields: ['address_components', 'geometry'],
        types: ['address'],
      });
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.geometry) return;
        const components = place.address_components || [];
        const get = (type: string) => components.find((c: any) => c.types.includes(type))?.long_name || '';
        setFormData(prev => ({
          ...prev,
          address: `${get('street_number')} ${get('route')}`.trim(),
          suburb: get('locality') || get('sublocality'),
          state: (() => {
            const stateMap: Record<string, string> = {
              'New South Wales': 'NSW', 'Victoria': 'VIC', 'Queensland': 'QLD',
              'Western Australia': 'WA', 'South Australia': 'SA', 'Tasmania': 'TAS',
              'Australian Capital Territory': 'ACT', 'Northern Territory': 'NT',
            };
            return stateMap[get('administrative_area_level_1')] || get('administrative_area_level_1');
          })(),
          postcode: get('postal_code'),
        }));
        addressSelectedFromAutocomplete.current = true;
        setLatitude(place.geometry.location.lat());
        setLongitude(place.geometry.location.lng());
      });
    };
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDOerzhhGJ4THjQkdH02A1OWxyMcrduJLw&libraries=places&loading=async`;
      script.async = true;
      script.onload = () => setTimeout(attachAutocomplete, 300);
      document.head.appendChild(script);
    } else {
      setTimeout(attachAutocomplete, 300);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.is_verified) {
      const goToVerify = confirm('You need to verify your identity before listing a space. Would you like to go to Settings to complete verification?');
      if (goToVerify) navigate('/settings');
      return;
    }
    if (latitude == null || longitude == null) {
      showToast('Could not geocode address. Please verify the address or select from suggestions.', 'error');
      return;
    }
    if (!formData.title.trim()) { showToast('Please enter a title for your listing', 'error'); return; }
    if (!validateAllFields()) return;

    const newErrors: Record<string, boolean> = {};
    if (!formData.title.trim()) newErrors.title = true;
    if (!formData.description.trim()) newErrors.description = true;
    if (!formData.spaceType) newErrors.spaceType = true;
    if (!formData.size) newErrors.size = true;
    if (!formData.address.trim()) newErrors.address = true;
    if (!formData.suburb.trim()) newErrors.suburb = true;
    if (!formData.state) newErrors.state = true;
    if (!formData.postcode.trim()) newErrors.postcode = true;
    if (!formData.price) newErrors.price = true;
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsSubmitting(true);
    try {
      if (!user) throw new Error('Not authenticated');
      const finalImageUrls: string[] = existingImages.filter(url => !deletedExistingImages.includes(url));
      if (uploadedFiles.length > 0) {
        for (let i = 0; i < uploadedFiles.length; i++) {
          const file = uploadedFiles[i];
          const validation = validateImageFile(file, i);
          if (!validation.valid) { showToast(validation.error!, 'error'); setIsSubmitting(false); return; }
        }
        const totalFiles = uploadedFiles.length;
        for (let i = 0; i < uploadedFiles.length; i++) {
          const file = uploadedFiles[i];
          showToast(`Uploading images... (${i + 1}/${totalFiles})`, 'info');
          const fileExt = file.name.split('.').pop();
          const fileName = `${user.id}/${Date.now()}_${i}.${fileExt}`;
          const { data, error } = await supabase.storage.from('listing-images').upload(fileName, file, { cacheControl: '3600', upsert: false });
          if (error) throw new Error(`Failed to upload image ${i + 1}: ${error.message}`);
          const { data: { publicUrl } } = supabase.storage.from('listing-images').getPublicUrl(fileName);
          finalImageUrls.push(publicUrl);
        }
        showToast('Images uploaded successfully', 'success');
      }

      let availabilityDate = new Date().toISOString();
      if (formData.availability === 'custom' && formData.customAvailabilityDate) {
        availabilityDate = new Date(formData.customAvailabilityDate).toISOString();
      } else if (formData.availability === '1week') {
        const d = new Date(); d.setDate(d.getDate() + 7); availabilityDate = d.toISOString();
      } else if (formData.availability === '2weeks') {
        const d = new Date(); d.setDate(d.getDate() + 14); availabilityDate = d.toISOString();
      } else if (formData.availability === '1month') {
        const d = new Date(); d.setMonth(d.getMonth() + 1); availabilityDate = d.toISOString();
      }

      const listingData = {
        title: formData.title,
        description: formData.description,
        space_type: formData.spaceType,
        size_sqm: parseFloat(formData.size),
        street_address: formData.address,
        suburb: formData.suburb,
        state: formData.state,
        postcode: formData.postcode,
        latitude: latitude,
        longitude: longitude,
        price_per_month: parseFloat(formData.price),
        features: formData.features,
        available_from: availabilityDate.split('T')[0],
        available_to: formData.rentalEndDate ? formData.rentalEndDate : null,
        min_rental_period: formData.minRentalPeriod === 'flexible' ? 1 : parseInt(formData.minRentalPeriod),
        images: finalImageUrls,
        first_month_discount: formData.firstMonthDiscount,
        status: 'active',
        is_available: true,
        exposure: formData.exposure,
      };

      if (editingId) {
        const { data: listing, error: dbError } = await supabase.from('listings').update(listingData).eq('id', editingId).eq('host_id', user.id).select().single();
        if (dbError) throw dbError;
        showToast('Listing updated successfully!', 'success');
      } else {
        const { data: listing, error: dbError } = await supabase.from('listings').insert({ host_id: user.id, ...listingData }).select().single();
        if (dbError) throw dbError;
        showToast('Listing created successfully!', 'success');
      }

      setShowSuccess(true);

      if (!editingId) {
        const { data: profileData } = await supabase.from('profiles').select('stripe_account_id').eq('id', user.id).single();
        if (!profileData?.stripe_account_id) {
          setTimeout(() => { setShowSuccess(false); setShowBankModal(true); }, 1500);
        } else {
          setTimeout(() => navigate('/my-listings'), 2000);
        }
      } else {
        setTimeout(() => navigate('/my-listings'), 2000);
      }
    } catch (error: any) {
      showToast(`Failed to save listing: ${error.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const earnings = useMemo(() => {
    const price = parseFloat(formData.price) || 0;
    const period = formData.minRentalPeriod;
    const months = period === 'no-minimum' || period === 'no-maximum' ? 1 : parseInt(period) || 1;
    const totalRent = price * months;
    let discount = 0;
    if (formData.firstMonthDiscount && months >= 2) discount = price * 0.5;
    const rentAfterDiscount = totalRent - discount;
    const serviceFee = rentAfterDiscount * 0.1;
    const earningsAmount = rentAfterDiscount - serviceFee;
    return { totalRent, discount, rentAfterDiscount, serviceFee, earnings: earningsAmount, months };
  }, [formData.price, formData.minRentalPeriod, formData.firstMonthDiscount]);

  if (!user) { navigate('/login'); return null; }

  if (isLoadingListing) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-36">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-12 h-12 border-4 border-[#14B8A6] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading listing...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-36">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-[#14B8A6] mb-6 cursor-pointer">
          <i className="ri-arrow-left-line"></i>
          <span className="font-medium">Back</span>
        </button>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            {editingId ? 'Edit Your Listing' : 'List Your Space'}
          </h1>
          <p className="text-lg text-gray-600">
            {editingId ? 'Update your storage space details' : 'Start earning by renting out your unused storage space'}
          </p>
        </div>

        {editingId && (
          <div className="flex gap-3 mb-6">
            <button type="button" onClick={handlePauseListing} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-pause-circle-line"></i>Pause Listing
            </button>
            <button type="button" onClick={handleDeleteListing} className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-delete-bin-line"></i>Delete Listing
            </button>
          </div>
        )}

        {showValidationSummary && Object.keys(validationErrors).length > 0 && (
          <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-start gap-3">
            <i className="ri-error-warning-line text-red-600 text-xl flex-shrink-0 mt-0.5"></i>
            <div className="flex-1">
              <p className="text-red-800 font-semibold mb-1">Please fix the highlighted fields</p>
              <p className="text-red-700 text-sm">Some required fields are missing or invalid. Please review and correct them before submitting.</p>
            </div>
            <button onClick={() => setShowValidationSummary(false)} className="text-red-600 hover:text-red-800 cursor-pointer">
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Section 1: Basic Information */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
              <div className="w-10 h-10 bg-[#14B8A6] text-white rounded-full flex items-center justify-center font-bold">1</div>
              <h2 className="text-2xl font-bold text-gray-900">Basic Information</h2>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Space Title <span className="text-red-500">*</span></label>
              <input type="text" name="title" value={formData.title} onChange={handleInputChange} onBlur={(e) => handleFieldBlur('title', e.target.value)} placeholder="e.g., Secure Garage Storage in Sydney CBD"
                className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm ${validationErrors.title || errors.title ? 'border-red-500' : 'border-gray-300'}`} required />
              {validationErrors.title && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><i className="ri-error-warning-line"></i>{validationErrors.title}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Description <span className="text-red-500">*</span></label>
              <textarea name="description" value={formData.description} onChange={handleInputChange} onBlur={(e) => handleFieldBlur('description', e.target.value)} placeholder="Describe your storage space, its condition, and any special features..." rows={5}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm ${validationErrors.description || errors.description ? 'border-red-500' : 'border-gray-300'}`} required></textarea>
              {validationErrors.description && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><i className="ri-error-warning-line"></i>{validationErrors.description}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Space Type <span className="text-red-500">*</span></label>
              {validationErrors.spaceType && <p className="text-red-600 text-sm mb-2 flex items-center gap-1"><i className="ri-error-warning-line"></i>{validationErrors.spaceType}</p>}
              {errors.spaceType && !validationErrors.spaceType && <p className="text-red-500 text-sm mb-2">Please select a space type</p>}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {spaceTypes.map((type) => (
                  <button key={type.id} type="button"
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, spaceType: type.id }));
                      if (errors.spaceType) setErrors((prev) => ({ ...prev, spaceType: false }));
                      if (validationErrors.spaceType) setValidationErrors((prev) => { const newErr = { ...prev }; delete newErr.spaceType; return newErr; });
                    }}
                    onBlur={() => handleFieldBlur('spaceType', formData.spaceType)}
                    className={`p-4 border-2 rounded-lg flex flex-col items-center gap-2 transition-all cursor-pointer ${
                      formData.spaceType === type.id ? 'border-[#14B8A6] bg-[#14B8A6]/10'
                      : validationErrors.spaceType || errors.spaceType ? 'border-red-500 bg-red-50'
                      : `border-gray-200 hover:border-gray-300 ${type.color}`}`}>
                    <div className="w-12 h-12 flex items-center justify-center"><i className={`${type.icon} text-2xl`}></i></div>
                    <span className={`text-sm font-semibold text-center ${formData.spaceType === type.id ? 'text-[#14B8A6]' : 'text-gray-700'}`}>{type.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Size or Dimensions of space in square metres <span className="text-red-500">*</span></label>
              <input type="number" name="size" value={formData.size} onChange={handleInputChange} placeholder="e.g., 20"
                className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm ${errors.size ? 'border-red-500' : 'border-gray-300'}`} required />
            </div>
          </div>

          {/* Section 2: Location Details */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
              <div className="w-10 h-10 bg-[#14B8A6] text-white rounded-full flex items-center justify-center font-bold">2</div>
              <h2 className="text-2xl font-bold text-gray-900">Location Details of Storage site</h2>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Street Address <span className="text-red-500">*</span></label>
              <input id="street-address-input" type="text" name="address" value={formData.address} onChange={handleInputChange} onBlur={handleAddressBlur} placeholder="Start typing your address..."
                className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm ${errors.address ? 'border-red-500' : 'border-gray-300'}`} required autoComplete="off" />
              {addressSelectedFromAutocomplete.current ? (
                <p className="text-xs text-green-600 flex items-center gap-1 mt-1"><i className="ri-checkbox-circle-line"></i>Address verified — location confirmed from Google</p>
              ) : formData.address.trim() ? (
                <p className="text-xs text-amber-600 flex items-center gap-1 mt-1"><i className="ri-error-warning-line"></i>For accurate map placement, please select your address from the dropdown suggestions</p>
              ) : (
                <p className="text-xs text-gray-500 flex items-center gap-1 mt-1"><i className="ri-map-pin-line"></i>Start typing and select from the suggestions for accurate map placement</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Suburb <span className="text-red-500">*</span></label>
                <input type="text" name="suburb" value={formData.suburb} onChange={() => {}} readOnly onBlur={(e) => handleFieldBlur('suburb', e.target.value)} placeholder="Sydney"
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm ${validationErrors.suburb || errors.suburb ? 'border-red-500' : 'border-gray-300'}`} required />
                {validationErrors.suburb && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><i className="ri-error-warning-line"></i>{validationErrors.suburb}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">State <span className="text-red-500">*</span></label>
                <select name="state" value={formData.state} onChange={() => {}}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm bg-gray-50 cursor-not-allowed ${validationErrors.state || errors.state ? 'border-red-500' : 'border-gray-300'}`} required>
                  <option value="">Select State</option>
                  <option value="NSW">NSW</option><option value="VIC">VIC</option><option value="QLD">QLD</option>
                  <option value="WA">WA</option><option value="SA">SA</option><option value="TAS">TAS</option>
                  <option value="ACT">ACT</option><option value="NT">NT</option>
                </select>
                {validationErrors.state && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><i className="ri-error-warning-line"></i>{validationErrors.state}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Postcode <span className="text-red-500">*</span></label>
              <input type="text" name="postcode" value={formData.postcode} onChange={() => {}} readOnly onBlur={(e) => handleFieldBlur('postcode', e.target.value)} placeholder="2000"
                className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm ${validationErrors.postcode || errors.postcode ? 'border-red-500' : 'border-gray-300'}`} required />
              {validationErrors.postcode && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><i className="ri-error-warning-line"></i>{validationErrors.postcode}</p>}
            </div>
          </div>

          {/* Section 3: Additional Details */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
              <div className="w-10 h-10 bg-[#14B8A6] text-white rounded-full flex items-center justify-center font-bold">3</div>
              <h2 className="text-2xl font-bold text-gray-900">Additional Details</h2>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Features & Amenities <span className="text-red-500 font-semibold text-base ml-2">(Select at least 3 features to publish your listing)</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {availableFeatures.map((feature) => (
                  <button key={feature} type="button" onClick={() => toggleFeature(feature)}
                    className={`p-3 border-2 rounded-lg text-sm font-medium transition-all cursor-pointer ${formData.features.includes(feature) ? 'border-[#14B8A6] bg-[#14B8A6]/5 text-[#14B8A6]' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                    <i className={`ri-checkbox-circle-${formData.features.includes(feature) ? 'fill' : 'line'} mr-1`}></i>
                    {feature}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Exposure Type <span className="text-red-500">*</span></label>
              <div className="flex gap-3">
                {['Indoor', 'Outdoor - covered', 'Outdoor - uncovered'].map((option) => (
                  <button key={option} type="button" onClick={() => setFormData(prev => ({ ...prev, exposure: option }))}
                    className={`flex-1 py-3 px-2 rounded-lg border-2 text-sm font-semibold transition-colors cursor-pointer ${formData.exposure === option ? 'border-[#14B8A6] bg-[#14B8A6] text-white' : 'border-gray-300 text-gray-700 hover:border-[#14B8A6]'}`}>
                    {option}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Availability <span className="text-red-500">*</span></label>
              <select name="availability" value={formData.availability} onChange={handleInputChange} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm" required>
                <option value="immediate">Available Immediately</option>
                <option value="1week">Available in 1 Week</option>
                <option value="2weeks">Available in 2 Weeks</option>
                <option value="1month">Available in 1 Month</option>
                <option value="custom">Choose Custom Date</option>
              </select>
              {formData.availability === 'custom' && (
                <div className="mt-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Select Available Date <span className="text-red-500">*</span></label>
                  <input type="date" name="customAvailabilityDate" value={formData.customAvailabilityDate} onChange={handleInputChange} min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm" required />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">End Rental Date <span className="text-gray-400 text-xs font-normal">(optional — leave blank if ongoing)</span></label>
              <input type="date" name="rentalEndDate" value={formData.rentalEndDate} onChange={handleInputChange} min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm" />
              <p className="text-xs text-gray-500 mt-1">Set a date if you only want to rent your space until a specific date.</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Minimum Rental Period <span className="text-red-500">*</span></label>
              <select name="minRentalPeriod" value={formData.minRentalPeriod} onChange={handleInputChange} onBlur={(e) => handleFieldBlur('minRentalPeriod', e.target.value)}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm ${validationErrors.minRentalPeriod ? 'border-red-500' : 'border-gray-300'}`} required>
                <option value="1">1 Month</option><option value="2">2 Months</option><option value="3">3 Months</option>
                <option value="6">6 Months</option><option value="12">12 Months</option>
                <option value="no-minimum">No minimum rental period</option><option value="no-maximum">No maximum rental period</option>
              </select>
              {validationErrors.minRentalPeriod && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><i className="ri-error-warning-line"></i>{validationErrors.minRentalPeriod}</p>}
            </div>
          </div>

          {/* Section 4: Photos */}
          <PhotoUploadSection imagePreviews={imagePreviews} onImageUpload={handleImageUpload} onRemovePhoto={removePhoto} />

          {/* Section 5: Pricing */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
              <div className="w-10 h-10 bg-[#14B8A6] text-white rounded-full flex items-center justify-center font-bold">5</div>
              <h2 className="text-2xl font-bold text-gray-900">Pricing</h2>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Monthly Price (AUD) <span className="text-red-500">*</span></label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">$</span>
                <input type="number" name="price" value={formData.price} onChange={handleInputChange} onBlur={(e) => handleFieldBlur('price', e.target.value)} placeholder="500"
                  className={`w-full pl-8 pr-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm ${validationErrors.price || errors.price ? 'border-red-500' : 'border-gray-300'}`} required />
              </div>
              {validationErrors.price && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><i className="ri-error-warning-line"></i>{validationErrors.price}</p>}
              <p className="text-xs text-gray-500 mt-2">Set a competitive price for your space</p>
            </div>

            <div className="flex items-center">
              <input type="checkbox" id="firstMonthDiscount" checked={formData.firstMonthDiscount}
                onChange={(e) => setFormData((prev) => ({ ...prev, firstMonthDiscount: e.target.checked }))}
                className="w-4 h-4 text-[#14B8A6] bg-gray-100 border-gray-300 rounded focus:ring-[#14B8A6] cursor-pointer" />
              <label htmlFor="firstMonthDiscount" className="ml-2 text-sm font-medium text-gray-700 cursor-pointer">50% off 1st month</label>
            </div>

            <div className="bg-[#14B8A6]/5 border border-[#14B8A6]/20 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-3">Estimated Earnings</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Monthly rent × {earnings.months} month{earnings.months !== 1 ? 's' : ''}</span>
                  <span className="font-semibold text-gray-900">${earnings.totalRent.toFixed(2)}</span>
                </div>
                {formData.firstMonthDiscount && earnings.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">50% off 1st month discount</span>
                    <span className="font-semibold text-green-600">-${earnings.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Service fee (10%)</span>
                  <span className="font-semibold text-gray-900">-${earnings.serviceFee.toFixed(2)}</span>
                </div>
                <div className="border-t border-gray-300 pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="font-bold text-gray-900">You earn</span>
                    <span className="font-bold text-[#14B8A6] text-xl">${earnings.earnings.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              {formData.firstMonthDiscount && earnings.months < 2 && (
                <p className="text-xs text-amber-600 mt-3 italic">* 50% discount applies only for rental periods of 2 months or more</p>
              )}
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end pt-6 border-t border-gray-200">
            <div className="w-full">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={declarationChecked} onChange={(e) => setDeclarationChecked(e.target.checked)}
                    className="w-5 h-5 mt-0.5 text-[#14B8A6] border-gray-300 rounded focus:ring-[#14B8A6] flex-shrink-0" />
                  <span className="text-sm text-amber-900">
                    <strong>Host Declaration:</strong> I confirm that I own this property or have the legal right to rent it out as storage space. I understand that false declarations may result in account suspension and legal action.
                  </span>
                </label>
              </div>
              {!isFormComplete() && !isSubmitting && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-error-warning-line text-amber-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-amber-800 font-semibold mb-2">Please complete the following before {editingId ? 'updating' : 'publishing'}:</p>
                      <ul className="space-y-1">
                        {getMissingFields().map((field, i) => (
                          <li key={i} className="text-amber-700 text-sm flex items-center gap-2">
                            <i className="ri-checkbox-blank-circle-line text-xs"></i>{field}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex justify-end">
                <button type="submit" disabled={isSubmitting || !isFormComplete()}
                  className="px-8 py-3 bg-[#14B8A6] text-white font-semibold rounded-lg hover:bg-[#0F9B8E] transition-colors cursor-pointer whitespace-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed">
                  {isSubmitting ? (
                    <><i className="ri-loader-4-line animate-spin mr-2"></i>{editingId ? 'Updating...' : 'Uploading...'}</>
                  ) : (
                    <><i className="ri-check-line mr-2"></i>{editingId ? 'Update Listing' : 'Publish Listing'}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <Footer />

      {/* Bank Account Warning Modal */}
      {showBankModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl p-8 max-w-md w-full">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-bank-line text-3xl text-amber-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">Connect Your Bank Account</h3>
            <p className="text-gray-600 mb-2 text-center">Your listing is live! 🎉</p>
            <p className="text-gray-600 mb-6 text-center text-sm">
              To receive payouts when someone rents your space, you need to connect your Australian bank account via Stripe. Without this, payments cannot be transferred to you.
            </p>
            <div className="flex flex-col gap-3">
              <button onClick={() => navigate('/earnings')}
                className="w-full py-3 bg-[#14B8A6] text-white font-semibold rounded-lg hover:bg-[#0F9B8E] transition-colors cursor-pointer flex items-center justify-center gap-2">
                <i className="ri-bank-line"></i>Connect Bank Account Now
              </button>
              <button onClick={() => navigate('/my-listings')}
                className="w-full py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors cursor-pointer">
                I'll Do This Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-3xl text-green-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{editingId ? 'Listing Updated!' : 'Listing Created!'}</h3>
            <p className="text-gray-600 mb-6">
              {editingId ? 'Your storage space has been successfully updated. Redirecting to your listings...' : 'Your storage space has been successfully listed. Redirecting to your listings...'}
            </p>
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 bg-[#14B8A6] rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-[#14B8A6] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-[#14B8A6] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
